import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Plus, Edit, Trash2, Loader2, ExternalLink } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { LOCALES, LOCALE_LABELS } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  adminListAllPosts,
  adminGetPost,
  adminUpsertPost,
  adminDeletePost,
  BLOG_CATEGORIES,
  type BlogCategorySlug,
} from "@/lib/blog.functions";

export const Route = createFileRoute("/admin_/blog")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({ meta: [{ title: "Blog admin — Recovery Trauma™" },
      { name: "robots", content: "noindex,nofollow" },
    ] }),
  component: AdminBlogPage,
});

type PostRow = {
  id: string;
  slug: string;
  title: string;
  category: BlogCategorySlug;
  status: string;
  published_at: string | null;
  updated_at: string;
  lang: string;
};

function AdminBlogPage() {
  const listFn = useServerFn(adminListAllPosts);
  const qc = useQueryClient();
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["admin", "blog", "list"],
    queryFn: () => listFn({}),
  });

  const deleteFn = useServerFn(adminDeletePost);
  const del = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Post deleted");
      qc.invalidateQueries({ queryKey: ["admin", "blog"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [langFilter, setLangFilter] = useState<string>("all");

  const visiblePosts = (posts as PostRow[]).filter(
    (p) => langFilter === "all" || (p.lang || "en") === langFilter,
  );


  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-3.5 w-3.5" /> Admin
          </Link>
          <h1 className="mt-1 text-2xl font-semibold">Blog</h1>
          <p className="text-sm text-muted-foreground">Public articles at /blog</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={langFilter} onValueChange={setLangFilter}>
            <SelectTrigger className="w-[190px]" aria-label="Filter by language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All languages</SelectItem>
              {LOCALES.map((l) => (
                <SelectItem key={l} value={l}>
                  {LOCALE_LABELS[l].flag} {LOCALE_LABELS[l].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setCreating(true)}>
            <Plus className="me-1.5 h-4 w-4" /> New post
          </Button>
        </div>
      </div>


      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-start text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Language</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Updated</th>
                <th className="px-4 py-2 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visiblePosts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No articles in this language yet.
                  </td>
                </tr>
              )}
              {visiblePosts.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-muted-foreground">/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {LOCALE_LABELS[(p.lang || "en") as (typeof LOCALES)[number]]?.flag}{" "}
                    {LOCALE_LABELS[(p.lang || "en") as (typeof LOCALES)[number]]?.label ?? p.lang}
                  </td>
                  <td className="px-4 py-3 text-xs">{p.category}</td>
                  <td className="px-4 py-3">
                    <Badge variant={p.status === "published" ? "default" : "secondary"}>
                      {p.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(p.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-end">
                    <div className="inline-flex gap-1">
                      {p.status === "published" && (
                        <a
                          href={
                            p.lang && p.lang !== "en"
                              ? `/${p.lang}/blog/${p.slug}`
                              : `/blog/${p.slug}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-muted"
                          aria-label="View"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        onClick={() => setEditingId(p.id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-muted"
                        aria-label="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete "${p.title}"?`)) del.mutate(p.id);
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded text-destructive hover:bg-destructive/10"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {posts.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center text-muted-foreground">
                    No posts yet. Click "New post" to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(editingId || creating) && (
        <PostEditorDialog
          id={editingId}
          onClose={() => {
            setEditingId(null);
            setCreating(false);
          }}
          onSaved={() => qc.invalidateQueries({ queryKey: ["admin", "blog"] })}
        />
      )}
    </div>
  );
}

function PostEditorDialog({
  id,
  onClose,
  onSaved,
}: {
  id: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const getFn = useServerFn(adminGetPost);
  const { data: post, isLoading } = useQuery({
    queryKey: ["admin", "blog", "post", id],
    queryFn: () => (id ? getFn({ data: { id } }) : Promise.resolve(null)),
    enabled: !!id,
  });

  const [form, setForm] = useState<{
    title: string;
    slug: string;
    excerpt: string;
    body_markdown: string;
    meta_title: string;
    meta_description: string;
    cover_image_url: string;
    category: BlogCategorySlug;
    tags: string;
    author_name_override: string;
    status: "draft" | "published";
    lang: string;
  } | null>(null);

  if (id && isLoading) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogTitle className="sr-only">Loading post</DialogTitle>
          <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin" />
        </DialogContent>
      </Dialog>
    );
  }

  const initial = form ?? {
    title: post?.title ?? "",
    slug: post?.slug ?? "",
    excerpt: post?.excerpt ?? "",
    body_markdown: post?.body_markdown ?? "",
    meta_title: post?.meta_title ?? "",
    meta_description: post?.meta_description ?? "",
    cover_image_url: post?.cover_image_url ?? "",
    category: (post?.category as BlogCategorySlug) ?? "trauma-recovery",
    tags: (post?.tags ?? []).join(", "),
    author_name_override: post?.author_name_override ?? "",
    status: (post?.status as "draft" | "published") ?? "draft",
    lang: ((post as { lang?: string } | null)?.lang) ?? "en",
  };
  const f = form ?? initial;
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setForm({ ...(form ?? initial), [k]: v });

  const upsertFn = useServerFn(adminUpsertPost);
  const save = useMutation({
    mutationFn: () =>
      upsertFn({
        data: {
          id: id ?? undefined,
          title: f.title,
          slug: f.slug || undefined,
          excerpt: f.excerpt,
          body_markdown: f.body_markdown,
          meta_title: f.meta_title,
          meta_description: f.meta_description,
          cover_image_url: f.cover_image_url,
          category: f.category,
          tags: f.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          author_name_override: f.author_name_override,
          status: f.status,
          lang: f.lang,
        },
      }),
    onSuccess: () => {
      toast.success("Post saved");
      onSaved();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{id ? "Edit post" : "New post"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label>Title</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Slug (URL)</Label>
              <Input
                value={f.slug}
                onChange={(e) => set("slug", e.target.value)}
                placeholder="auto from title"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v as BlogCategorySlug)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BLOG_CATEGORIES.map((c) => (
                    <SelectItem key={c.slug} value={c.slug}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Excerpt (shown on cards & used as fallback description)</Label>
            <Textarea rows={2} value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
          </div>
          <div>
            <Label>Body (Markdown)</Label>
            <Textarea
              rows={14}
              value={f.body_markdown}
              onChange={(e) => set("body_markdown", e.target.value)}
              className="font-mono text-xs"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Cover image URL</Label>
              <Input value={f.cover_image_url} onChange={(e) => set("cover_image_url", e.target.value)} />
            </div>
            <div>
              <Label>Tags (comma separated)</Label>
              <Input value={f.tags} onChange={(e) => set("tags", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Meta title (SEO)</Label>
              <Input value={f.meta_title} onChange={(e) => set("meta_title", e.target.value)} />
            </div>
            <div>
              <Label>Meta description (SEO)</Label>
              <Input value={f.meta_description} onChange={(e) => set("meta_description", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Author name (override)</Label>
              <Input
                value={f.author_name_override}
                onChange={(e) => set("author_name_override", e.target.value)}
                placeholder="Recovery Trauma™"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v as "draft" | "published")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Language</Label>
            <Select value={f.lang} onValueChange={(v) => set("lang", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOCALES.map((l) => (
                  <SelectItem key={l} value={l}>
                    {LOCALE_LABELS[l].flag} {LOCALE_LABELS[l].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              This article is listed only on this language's blog.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !f.title}>
            {save.isPending && <Loader2 className="me-1.5 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
